// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckmarkCircle2Fill from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { forwardRef, ForwardRefRenderFunction, HTMLAttributes, PropsWithChildren, useMemo } from "react";
import { css } from "styled-components";

// Foundation --->
import uniqueId from "@insite/mobius/utilities/uniqueId";
// <--- Foundation

/* Store Custom Code - Start */
import DangerousFill from "@store/Library/Components/Icons/DangerousFill";
import WarningFill from "@store/Library/Components/Icons/WarningFill";
/* Store Custom Code - End */

export type InfosBoxTypeOption = DataOption;
export interface InfosBoxTypeOptionMapper {
    info: InfosBoxTypeOption;
    error: InfosBoxTypeOption;
    success: InfosBoxTypeOption;
    warning: InfosBoxTypeOption;
    // Foundation
    default: InfosBoxTypeOption;
}
export type InfosBoxTypeOptions = DataOptions<InfosBoxTypeOption> & InfosBoxTypeOptionMapper;
export type InfosBoxType = keyof InfosBoxTypeOptionMapper;

export const infosBoxTypeOptions: InfosBoxTypeOptions = {
    info: { id: "info", displayName: "Info" },
    error: { id: "error", displayName: "Error" },
    success: { id: "success", displayName: "Success" },
    warning: { id: "warning", displayName: "Warning" },
    // Foundation
    default: { id: "default", displayName: "Default", isDefault: true },
};

export type InfosBoxSizeOption = DataOption;
export interface InfosBoxSizeOptionMapper {
    medium: InfosBoxSizeOption;
    small: InfosBoxSizeOption;
    // Foundation
    tiny: InfosBoxSizeOption;
}
export type InfosBoxSizeOptions = DataOptions<InfosBoxSizeOption> & InfosBoxSizeOptionMapper;
export type InfosBoxSize = keyof InfosBoxSizeOptionMapper;

export const infosBoxSizeOptions: InfosBoxSizeOptions = {
    medium: { id: "medium", displayName: "Medium", isDefault: true },
    small: { id: "small", displayName: "Small" },
    // Foundation
    tiny: { id: "tiny", displayName: "Tiny" },
};

export interface InfosBoxProps extends ExpandStyles<InfosBoxStyles>, HTMLAttributes<HTMLElement> {
    type?: InfosBoxType;
    size?: InfosBoxSize;
    showIcon?: boolean;
    // Foundation
    id?: string;
}

export interface InfosBoxBorderStyles {
    color?: string;
    radius?: string;
    style?: string;
    width?: string;
}

export interface InfosBoxStyles {
    padding?: string;
    color?: string;
    backgroundColor?: string;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
    border?: InfosBoxBorderStyles;
}

export const infosBoxStyles: InfosBoxStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            width: 100%;
            display: flex;
            align-items: center;
            ${x => cssProp("line-height", x.theme.design.typography.lineHeight.semi)}
            border-radius: ${({ theme }) => theme.design.radius.small};

            ${({ theme }) => `
                ${cssProp("font-size", theme.design.typography.size.smaller)}
                ${cssProp("line-height", theme.design.typography.lineHeight.semi)}
            `}

            ul, ol, p {
                ${spacingStyles("0.5em", "0.5em")}
            }

            li {
                ${spacingStyles(null, "0.25em")}
            }

            ${breakpoint("sm")} {
                ${x => cssProp("font-size", x.theme.design.typography.size.small)}
            }

            a {
                font-weight: ${({ theme }) => theme.design.typography.weight.bold};
                text-decoration: underline;
            }

            a:active,
            a:visited {
                color: inherit;
            }

            a:hover {
                text-decoration: none;
            }
        `,
    },
    content: {
        css: css`
            width: 100%;
        `,
    },
    icon: {
        color: "currentColor",
    },
};

export const infosBoxTypes: Partial<Record<InfosBoxType, InfosBoxStyles>> = {
    info: {
        color: "info",
        backgroundColor: "info.light",
        icon: {
            src: InfoFill,
            color: "info",
        },
    },
    warning: {
        color: "warning",
        backgroundColor: "warning.light",
        icon: {
            src: WarningFill,
            color: "warning",
        },
    },
    error: {
        color: "danger",
        backgroundColor: "danger.light",
        icon: {
            src: DangerousFill,
            color: "danger",
        },
    },
    success: {
        color: "success",
        backgroundColor: "success.light",
        icon: {
            src: CheckmarkCircle2Fill,
            color: "success",
        },
    },
    default: {
        color: "info",
        backgroundColor: "info.light",
        icon: {
            src: CheckmarkCircle2Fill,
            color: "info",
        },
    },
};

export const infosBoxSizes: Partial<Record<InfosBoxSize, InfosBoxStyles>> = {
    small: {
        padding: "0.6em",
        icon: {
            size: 24,
            css: css`
                margin-right: 0.4em;
            `,
        },
        wrapper: {
            css: css`
                ${x => cssProp("font-size", x.theme.design.typography.size.smaller)}
            `,
        },
    },
    medium: {
        padding: "1em",
        icon: {
            size: 28,
            css: css`
                margin-right: 0.6667em;
            `,
        },
        wrapper: {
            css: css`
                ${x => cssProp("font-size", x.theme.design.typography.size.small)}
            `,
        },
    },
};

const InfosBox: ForwardRefRenderFunction<HTMLDivElement, PropsWithChildren<InfosBoxProps>> = (
    {
        type = "info",
        size = "medium",
        showIcon = true,
        children,
        extendedStyles,
        id,
        overridedStyles,
        ...htmlAttributes
    },
    ref,
) => {
    const definitions = useMergeProps(infosBoxSizes[size], infosBoxTypes[type]);
    const stylesAndDefinitions = useMergeProps(infosBoxStyles, definitions);
    const baseStyles = useExpandProps(stylesAndDefinitions, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<InfosBoxStyles>(baseStyles, {
            wrapper: {
                css: css`
                    ${({ theme }) => css`
                        ${cssProp("padding", baseStyles.padding)}
                        ${cssProp("color", baseStyles.color, x => resolveColor(x, theme))}
                        ${cssProp("background-color", baseStyles.backgroundColor, x => resolveColor(x, theme))}
                        ${cssProp("border-width", baseStyles.border?.width)}
                        ${cssProp("border-style", baseStyles.border?.style)}
                        ${cssProp("border-color", baseStyles.border?.color, x => resolveColor(x, theme))}
                        ${cssProp("border-radius", baseStyles.border?.radius)}
                    `}
                `,
            },
            icon: {
                css: css`
                    ${cssProp("margin-right", baseStyles.padding)}
                `,
            },
        });
    }, [baseStyles]);
    // Foundation --->
    const wrapperId = id || uniqueId();
    // <--- Foundation

    if (!children) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} id={wrapperId} {...htmlAttributes} ref={ref}>
            {showIcon && <Icon {...styles.icon} />}
            <StyledWrapper {...styles.content}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default forwardRef(InfosBox);
